import { type GoalOverview, GoalOverviewHorizon, GoalOverviewLifecycleStatus, type GoalsList } from "Infrastructure/ApiZfp/Schema";
import { startOfDay } from "date-fns";
import { toDate } from "./dates";

export type SortOrder = "asc" | "desc";

export type FutureHorizon = Exclude<GoalOverviewHorizon, typeof GoalOverviewHorizon.past>;

export type TimelineItem =
  | { type: "goal"; goal: GoalOverview }
  | { type: "today" }
  | { type: "horizon"; horizon: FutureHorizon; startDate: Date };

const FUTURE_HORIZONS: FutureHorizon[] = [GoalOverviewHorizon.short, GoalOverviewHorizon.middle, GoalOverviewHorizon.long];

const getTargetTime = (goal: GoalOverview) => (goal.targetDate ? toDate(goal.targetDate).getTime() : Number.MAX_SAFE_INTEGER);

export const sortByTargetDate = (goals: GoalOverview[], sortOrder: SortOrder) => {
  const ascending = [...goals].sort((a, b) => getTargetTime(a) - getTargetTime(b));
  return sortOrder === "asc" ? ascending : ascending.reverse();
};

// The date decides between past and future, the stored horizon can lag behind it. A goal due today isn't past yet.
const getHorizon = (goal: GoalOverview, now: Date): GoalOverviewHorizon => {
  if (!goal.targetDate) return goal.horizon;
  if (toDate(goal.targetDate) < startOfDay(now)) return GoalOverviewHorizon.past;
  return goal.horizon === GoalOverviewHorizon.past ? GoalOverviewHorizon.short : goal.horizon;
};

const TODAY_ITEM: TimelineItem = { type: "today" };

/**
 * Rows of the timeline: past goals and the today line after them (only when there are any), then every horizon
 * that has goals with its divider in front. Descending order is the exact mirror, so each divider ends up below its goals.
 */
export const buildTimelineItems = ({ goals, midTermPoint, longTermPoint }: GoalsList, now: Date, sortOrder: SortOrder): TimelineItem[] => {
  const activeGoals = sortByTargetDate(
    goals.filter((goal) => goal.lifecycleStatus !== GoalOverviewLifecycleStatus.archived),
    "asc",
  );

  if (activeGoals.length === 0) return [];

  const horizonStart: Record<FutureHorizon, Date> = {
    short: now,
    middle: toDate(midTermPoint.date),
    long: toDate(longTermPoint.date),
  };

  const goalItemsIn = (horizon: GoalOverviewHorizon): TimelineItem[] =>
    activeGoals.filter((goal) => getHorizon(goal, now) === horizon).map((goal) => ({ type: "goal", goal }));

  const pastGoalItems = goalItemsIn(GoalOverviewHorizon.past);

  const items: TimelineItem[] = [
    ...pastGoalItems,
    ...(pastGoalItems.length > 0 ? [TODAY_ITEM] : []),
    ...FUTURE_HORIZONS.flatMap((horizon): TimelineItem[] => {
      const goalItems = goalItemsIn(horizon);
      return goalItems.length > 0 ? [{ type: "horizon", horizon, startDate: horizonStart[horizon] }, ...goalItems] : [];
    }),
  ];

  return sortOrder === "asc" ? items : items.reverse();
};
